import { useEffect } from "react";
import { Link, useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import ClarityOneLayout from "@/components/clarity-one/ClarityOneLayout";
import { clarityOneLoginUrlWithRedirect } from "@shared/clarityOneAuthRedirect";
import { CLARITY_ONE_ASSISTANCE } from "@shared/clarityOneContact";
import {
  invoiceHref,
  partitionClarityOneInvoices,
} from "@shared/clarityOneInvoiceList";
import { formatDate } from "@/lib/dateUtils";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Download, ExternalLink, Loader2 } from "lucide-react";
import { formatStatementPaymentMethod } from "@shared/clarityOneStatementOfAccount";

function formatSgd(amount: number): string {
  return new Intl.NumberFormat("en-SG", { style: "currency", currency: "SGD" }).format(
    Number.isFinite(amount) ? amount : 0,
  );
}

function formatDueDate(dueDate: string | Date | null | undefined): string {
  if (!dueDate) return "—";
  if (typeof dueDate === "string" && /^\d{4}-\d{2}/.test(dueDate)) return dueDate;
  return formatDate(dueDate);
}

type InvoiceRow = {
  id: number;
  kind: "portal" | "deal";
  invoiceNumber: string;
  invoiceDate: string | Date | null;
  dueDate: string | Date | null;
  amount: number;
  token: string | null;
  paymentGroup: "outstanding" | "paid";
  displayStatus: "Outstanding" | "Partially paid" | "Paid";
  receiptUrl?: string | null;
};
type PaymentRow = { id: string; invoiceNumber: string | null; amount: number; paidAt: string; method: string | null; receiptUrl: string | null };

function InvoiceCard({ inv }: { inv: InvoiceRow }) {
  const href = invoiceHref(inv.kind, inv.token);
  return (
    <div
      className="min-w-0 bg-white rounded-2xl border border-[#E8E4DC] px-5 py-5 sm:px-6"
      data-testid={`clarity-one-invoice-${inv.kind}-${inv.id}`}
    >
      <p className="text-[11px] tracking-[0.15em] uppercase text-[#B8956E] mb-1 break-words">
        {inv.invoiceNumber}
      </p>
      <p className="text-xl font-medium text-[#1A2744] break-words">{formatSgd(inv.amount)}</p>
      <dl className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm text-[#8B7355]">
        <div className="min-w-0">
          <dt className="text-[10px] uppercase tracking-wide">Invoice date</dt>
          <dd className="text-[#1A2744] break-words">
            {inv.invoiceDate ? formatDate(inv.invoiceDate) : "—"}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-[10px] uppercase tracking-wide">Due date</dt>
          <dd className="text-[#1A2744] break-words">{formatDueDate(inv.dueDate)}</dd>
        </div>
        <div className="min-w-0">
          <dt className="text-[10px] uppercase tracking-wide">Status</dt>
          <dd className="text-[#1A2744]">{inv.displayStatus}</dd>
        </div>
      </dl>
      {href ? (
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            asChild
            size="sm"
            className="min-h-10 bg-[#1A2744] text-white hover:bg-[#1A2744]/90"
          >
            <Link href={href}>
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              View
            </Link>
          </Button>
          <Button asChild size="sm" variant="outline" className="min-h-10 border-[#E8E4DC]">
            <Link href={href}>
              <Download className="h-3.5 w-3.5 mr-1.5" />
              Download PDF
            </Link>
          </Button>
          {inv.receiptUrl && (
            <Button asChild size="sm" variant="outline" className="min-h-10 border-[#E8E4DC]">
              <a href={inv.receiptUrl}>
                <Download className="h-3.5 w-3.5 mr-1.5" /> Download receipt
              </a>
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
}

function InvoiceSection({
  title,
  count,
  invoices,
  emptyMessage,
  testId,
}: {
  title: string;
  count: number;
  invoices: InvoiceRow[];
  emptyMessage: string;
  testId: string;
}) {
  return (
    <section className="min-w-0" data-testid={testId}>
      <div className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-2xl font-light tracking-tight text-[#1A2744]">{title}</h2>
        <p className="text-sm text-[#8B7355]" data-testid={`${testId}-count`}>
          {count}
        </p>
      </div>
      {invoices.length === 0 ? (
        <p className="text-sm text-[#8B7355]" data-testid={`${testId}-empty`}>
          {emptyMessage}
        </p>
      ) : (
        <div className="space-y-4">
          {invoices.map((inv) => (
            <InvoiceCard key={`${inv.kind}-${inv.id}`} inv={inv} />
          ))}
        </div>
      )}
    </section>
  );
}

export default function ClarityOneInvoicesPage() {
  const [, setLocation] = useLocation();
  const { data: me, isLoading: meLoading } = trpc.clarityOneAuth.me.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
  });
  const { data, isLoading, isError, isFetching, refetch } = trpc.clarityOneRecords.listInvoices.useQuery(
    undefined,
    {
      enabled: !!me,
      refetchOnWindowFocus: false,
    },
  );
  const paymentsQuery = trpc.clarityOneRecords.listPayments.useQuery(undefined, { enabled: !!me, refetchOnWindowFocus: false });

  useEffect(() => {
    if (!meLoading && !me) setLocation(clarityOneLoginUrlWithRedirect("/invoices"));
  }, [meLoading, me, setLocation]);

  if (meLoading || !me) {
    return (
      <ClarityOneLayout showNav={false}>
        <div className="flex justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-[#B8956E]" />
        </div>
      </ClarityOneLayout>
    );
  }

  const invoices = (data ?? []) as InvoiceRow[];
  const hasLoadedInvoices = Array.isArray(data);
  const { outstanding, paid } = partitionClarityOneInvoices(invoices);
  const showInitialLoading = isLoading && !hasLoadedInvoices;
  const showHardFailure = isError && !hasLoadedInvoices;
  const showStaleWarning = isError && hasLoadedInvoices;
  const showGlobalEmpty = hasLoadedInvoices && !isError && invoices.length === 0;

  return (
    <ClarityOneLayout showNav>
      <div className="min-w-0 overflow-x-hidden" data-testid="clarity-one-invoices">
        <Link href="/home" className="inline-flex items-center gap-1 text-sm text-[#8B7355] no-underline mb-6 hover:text-[#1A2744]">
          <ChevronLeft className="h-4 w-4" /> Home
        </Link>
        <h1 className="text-3xl font-light text-[#1A2744] tracking-tight mb-10">My Invoices</h1>
        <section className="mb-8" data-testid="clarity-one-payments">
          <h2 className="text-2xl font-light tracking-tight text-[#1A2744] mb-4">Payments received</h2>
          {paymentsQuery.isLoading && <p className="text-sm text-[#8B7355]">Loading payments...</p>}
          {paymentsQuery.isError && <p className="text-sm text-[#8B7355]">Payments could not be refreshed. <button className="underline" onClick={() => void paymentsQuery.refetch()}>Try again</button></p>}
          {paymentsQuery.data?.length === 0 && !paymentsQuery.isError && <p className="text-sm text-[#8B7355]">No payments recorded yet.</p>}
          <div className="space-y-3">
            {(paymentsQuery.data as PaymentRow[] | undefined)?.map(payment => (
              <div key={payment.id} className="rounded-2xl border border-[#E8E4DC] bg-white p-5 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-[#1A2744]">{formatSgd(payment.amount)} received</p>
                  <p className="text-sm text-[#8B7355]">{formatDate(payment.paidAt)} · {payment.invoiceNumber || "Invoice"} · {formatStatementPaymentMethod(payment.method)}</p>
                </div>
                {payment.receiptUrl && <Button asChild size="sm" variant="outline"><a href={payment.receiptUrl}><Download className="h-3.5 w-3.5 mr-1.5" />Download receipt</a></Button>}
              </div>
            ))}
          </div>
        </section>
        {showHardFailure ? (
          <div data-testid="clarity-one-invoices-error">
            <p className="text-sm text-[#8B7355] mb-4">
              We could not load your invoices. Please try again, or report the issue so we can help.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                className="min-h-10 bg-[#1A2744] text-white hover:bg-[#1A2744]/90"
                onClick={() => {
                  void refetch();
                }}
                disabled={isFetching}
              >
                Retry
              </Button>
              <Button asChild size="sm" variant="outline" className="min-h-10 border-[#E8E4DC]">
                <a href={`${CLARITY_ONE_ASSISTANCE.emailHref}?subject=${encodeURIComponent("Clarity One invoices")}`}>
                  Report Issue
                </a>
              </Button>
            </div>
          </div>
        ) : null}

        {showStaleWarning ? (
          <p className="mb-6 text-sm text-[#8B7355]" data-testid="clarity-one-invoices-stale">
            These invoices may not be the latest. Retry to refresh.
          </p>
        ) : null}

        {showInitialLoading ? (
          <Loader2 className="h-6 w-6 animate-spin text-[#B8956E]" data-testid="clarity-one-invoices-loading" />
        ) : showHardFailure ? null : showGlobalEmpty ? (
          <p className="text-sm text-[#8B7355]" data-testid="clarity-one-invoices-none">
            No invoices yet.
          </p>
        ) : hasLoadedInvoices ? (
          <div className="space-y-10">
            <InvoiceSection
              title="Outstanding"
              count={outstanding.length}
              invoices={outstanding}
              emptyMessage="No outstanding invoices."
              testId="clarity-one-invoices-outstanding"
            />
            <InvoiceSection
              title="Paid"
              count={paid.length}
              invoices={paid}
              emptyMessage="No paid invoices yet."
              testId="clarity-one-invoices-paid"
            />
          </div>
        ) : null}
      </div>
    </ClarityOneLayout>
  );
}
